"use client";

import type { Variants } from "framer-motion";
import { motion } from "framer-motion";
import moment from "moment";
import React from "react";

import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/hooks/useTranslation";
import type { Apartment, Room } from "@/payload-types";
import type { BookingData } from "@/types";
import type { PayLoadData } from "@/utils/payloadUtil";

type Props = {
  formData: BookingData;
  onInputChange?: (data: BookingData) => void;
  rooms: PayLoadData<Room[]> | undefined;
  apartments: PayLoadData<Apartment[]> | undefined;
};

export default function BookingInfo({
  formData,
  onInputChange,
  rooms,
  apartments,
}: Props) {
  const { t } = useTranslation();
  const { language } = useLanguage();

  const itemVariants: Variants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.4,
        ease: "easeOut",
      },
    },
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={itemVariants}
      className="p-5"
    >
      <div className="flex flex-col justify-center gap-5">
        {/* travel dates */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <label
              htmlFor="arrival"
              className={`block font-medium text-gray-700 `}
            >
              {t({ de: "Anreisetag", en: "Arrival Date" })}{" "}
              <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              id="arrival"
              name="arrival"
              required
              className={`w-full rounded-full border-2 border-primary-500 bg-primary-50 px-6 py-4 focus:outline-none focus:ring-2`}
              value={formData.travelDates.arrival}
              min={moment().format("YYYY-MM-DD")}
              onChange={(
                e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
              ) => {
                const { value } = e.target;
                const { departure } = formData.travelDates;
                if (onInputChange) {
                  onInputChange({
                    ...formData,
                    travelDates: {
                      departure: !moment(departure).isAfter(value)
                        ? moment(value).add(1, "d").format("YYYY-MM-DD")
                        : departure,
                      arrival: value,
                    },
                  });
                }
              }}
            />
          </div>
          <div className="flex flex-col gap-2">
            <label
              htmlFor="departure"
              className={`block font-medium text-gray-700 `}
            >
              {t({ de: "Abreisetag", en: "Departure Date" })}{" "}
              <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              id="departure"
              name="departure"
              required
              className={`w-full rounded-full border-2 border-primary-500 bg-primary-50 px-6 py-4 focus:outline-none focus:ring-2`}
              value={formData.travelDates.departure}
              min={moment(formData.travelDates.arrival)
                .add(1, "d")
                .format("YYYY-MM-DD")}
              onChange={(
                e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
              ) => {
                const { value } = e.target;
                if (onInputChange) {
                  onInputChange({
                    ...formData,
                    travelDates: { ...formData.travelDates, departure: value },
                  });
                }
              }}
            />
          </div>
        </div>

        {/* people info */}
        <div className="grid grid-cols-2 gap-5">
          <div className="flex flex-col gap-2">
            <label
              htmlFor="adults"
              className={`block font-medium text-gray-700`}
            >
              {t({ de: "Erwachsene", en: "Adults" })}{" "}
              <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              id="adults"
              name="adults"
              required
              className={`no-spinner w-full rounded-full border-2 border-primary-500 bg-primary-50 px-6 py-4 focus:outline-none focus:ring-2`}
              value={formData.people.adults}
              onChange={(
                e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
              ) => {
                const { value } = e.target;
                if (onInputChange) {
                  onInputChange({
                    ...formData,
                    people: {
                      ...formData.people,
                      adults: parseInt(value || "0", 10),
                    },
                  });
                }
              }}
            />
          </div>
          <div className="flex flex-col gap-2">
            <label
              htmlFor="children"
              className={`block font-medium text-gray-700 `}
            >
              {t({ de: "Kinder", en: "Children" })}{" "}
              <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              id="children"
              name="children"
              required
              className={`no-spinner w-full rounded-full border-2 border-primary-500 bg-primary-50 px-6 py-4 focus:outline-none focus:ring-2`}
              value={formData.people.children}
              onChange={(
                e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
              ) => {
                const { value } = e.target;
                if (onInputChange) {
                  onInputChange({
                    ...formData,
                    people: {
                      ...formData.people,
                      children: parseInt(value || "0", 10),
                    },
                  });
                }
              }}
            />
          </div>
        </div>
        {/* accommodation  */}
        <div className="grid grid-cols-1 gap-5">
          <div className="flex flex-col gap-2">
            <label
              htmlFor="apartment"
              className={`block font-medium text-gray-700 `}
            >
              {t({ de: "Ferienwohnungen", en: "Apartment" })} /{" "}
              {t({ de: "Zimmer", en: "Room" })}{" "}
              <span className="text-red-500">*</span>
            </label>
            <select
              id="apartment"
              name="apartment"
              className={`w-full appearance-none rounded-full border-2 border-primary-500 bg-primary-50 px-6 py-4 focus:outline-none focus:ring-2`}
              required
              value={formData.accommodation.roomApartment}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                if (onInputChange) {
                  onInputChange({
                    ...formData,
                    accommodation: { roomApartment: e.target.value },
                  });
                }
              }}
            >
              <option value="">
                {t({
                  en: "Select Apartment / Room",
                  de: "Ferienwohnung / Zimmer Auswählen",
                })}
              </option>
              {(apartments?.[language] || []).map((apartment) => (
                <option key={apartment.id} value={apartment.name}>
                  {apartment.name}
                </option>
              ))}
              {(rooms?.[language] || []).map((room) => (
                <option key={room.id} value={room.name}>
                  {room.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <label
            htmlFor="specialRequest"
            className={`block font-medium text-gray-700 `}
          >
            {t({ de: "Weitere Angaben", en: "Special request" })}{" "}
          </label>
          <textarea
            id="specialRequest"
            name="specialRequest"
            rows={4}
            className={`w-full rounded-3xl border-2 border-primary-500 bg-primary-50 px-6 py-4 placeholder:text-primary-600 focus:outline-none focus:ring-2`}
            placeholder={t({
              de: "Weitere Angaben",
              en: "Special request",
            })}
            value={formData.specialRequest || ""}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              const { value } = e.target;
              if (onInputChange) {
                onInputChange({ ...formData, specialRequest: value });
              }
            }}
          />
        </div>
      </div>
    </motion.div>
  );
}
